import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Heart, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ForgotForm } from "@/components/passwordReset";
import { apiError } from "@/lib/api";

function JoinForm({ onBack, create }) {
  const { register, receiptsOnly } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "", invite_code: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await register(create ? { ...f, invite_code: null } : f); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  const field = (k, label, type = "text") => (
    <label className="block text-sm">{label}
      <input className="field mt-1 w-full" type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`join-${k}-input`} required minLength={k === "password" ? 8 : undefined} />
    </label>
  );
  return (
    <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="join-form">
      <div><h2 className="font-heading text-2xl font-bold">{create ? "Create your household" : "Join a household"}</h2>
        <p className="text-sm text-muted-foreground">{create ? "Create your account. Then invite your family" : "Use the invite code shared by the household owner."}</p></div>
      {!create && field("invite_code", "Invite code")}{field("name", "Your name")}{field("email", "Email", "email")}{field("password", "Password (min 8 characters)", "password")}
      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-testid="join-error">{error}</div>}
      <button disabled={busy} className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90" data-testid="join-submit-btn">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {create ? "Create household" : "Join household"}
      </button>
      <button type="button" onClick={onBack} className="w-full text-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-login-btn">Back to sign in</button>
    </form>
  );
}

export default function Login() {
  const { user, login, receiptsOnly } = useAuth();
  const [joining, setJoining] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <div className="flex items-center justify-between">
          <div className="font-heading text-xl font-extrabold">Frugl<span className="text-emerald-300">Fam</span></div>
          <Link to="/about" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/20" data-testid="login-about-link"><Heart className="h-3.5 w-3.5 fill-rose-400 text-rose-400" /> About FruglFam</Link>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold leading-tight lg:text-5xl" data-testid="login-headline">Keep more of<br />what you earn.</h1>
          <p className="mt-4 max-w-md text-primary-foreground/80" data-testid="login-description">{receiptsOnly ? "Snap your receipts. FruglFam tracks what your groceries cost, builds your shopping list, finds the cheapest store nearby and answers your questions from your real receipts." : "Statements, receipts and bills in one place. FruglFam spots price drops, forgotten subscriptions and double charges, and answers your money questions from your real records."}</p>
          <p className="mt-6 text-sm font-semibold tracking-wide text-emerald-300">Spend smarter. Save more.</p>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center p-6">
        <div className="mb-10 w-full max-w-sm lg:hidden" data-testid="login-mobile-brand">
          <div className="font-heading text-3xl font-extrabold tracking-tight">Frugl<span className="text-primary">Fam</span></div>
          <div className="mt-1 text-sm font-semibold text-emerald-700">Spend smarter. Save more.</div>
        </div>
        {joining === "forgot" ? <ForgotForm initialEmail={email} onBack={() => setJoining(null)} /> : joining ? <JoinForm create={joining === "create"} onBack={() => setJoining(null)} /> : <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <h2 className="font-heading text-2xl font-bold">Sign in</h2>
          </div>
          <label className="block text-sm">Email
            <input className="field mt-1 w-full" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" required />
          </label>
          <div className="text-sm">
            <div className="flex items-center justify-between"><label htmlFor="login-password">Password</label>
              <button type="button" onClick={() => setJoining("forgot")} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</button></div>
            <input id="login-password" className="field mt-1 w-full" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" required />
          </div>
          {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-testid="login-error">{error}</div>}
          <button disabled={busy} className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90" data-testid="login-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <button type="button" onClick={() => setJoining("join")} className="w-full text-center text-sm text-primary hover:underline" data-testid="show-join-btn">Have an invite code? Join a household</button>
          <button type="button" onClick={() => setJoining("create")} className="w-full text-center text-sm text-muted-foreground hover:text-foreground" data-testid="show-create-btn">New here? Create your own household</button>
        </form>}
        <p className="mt-8 text-center text-xs text-muted-foreground">By using FruglFam you agree to our <Link to="/terms" className="text-primary underline" data-testid="login-terms-link">Terms of Service</Link> and <Link to="/privacy" className="text-primary underline" data-testid="login-privacy-link">Privacy Policy</Link>.</p>
      </div>
    </div>
  );
}
